import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, of, ReplaySubject} from 'rxjs';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Router} from '@angular/router';
import {FormHelperService} from '../../../shared/services/form-helper.service';
import {delay, first, skipUntil, takeUntil, tap} from 'rxjs/operators';
import {Uuid} from '../../../shared/interface/response/uuid';
import {HttpErrorResponse} from '@angular/common/http';
import {Ofwca} from '../../../shared/interface/ofwca';
import {KrkService} from '../../../shared/services/krk.service';
import {Krk} from '../../../shared/interface/krk';
import {
  DICT_TYPE_KRK_PAYER, DICT_TYPE_KRK_STATUS,
  DictService,
  KRK_PAYER_ASF,
  KRK_PAYER_PARTNER,
  KRK_STATUS_ACTIVE, KRK_STATUS_INACTIVE
} from '../../../shared/services/dict.service';
import {MatDialogRef} from '@angular/material/dialog';
import {KrkAddDialogComponent} from '../krk-add-dialog/krk-add-dialog.component';
import moment from 'moment';
import {OfwcaService} from '../../../shared/services/ofwca.service';

@Component({
  selector: 'app-krk-add',
  templateUrl: './krk-add.component.html',
  styleUrls: ['./krk-add.component.scss']
})
export class KrkAddComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private initialized: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() ofwcaObject?: Ofwca;
  @Input() krk?: Observable<Krk>;
  @Input() dialogRef?: MatDialogRef<KrkAddDialogComponent>;
  @Output() closeModification = new EventEmitter<any>();
  @Output() krkCreated = new EventEmitter<string>();

  krkObject: Krk;
  krkForm: FormGroup;
  ofwcaControl: FormControl;
  payerControl: FormControl;
  statusControl: FormControl;
  activeToControl: FormControl;
  activeFromControl: FormControl;
  activeFeeControl: FormControl;
  processingRequest = false;
  errors: any = {};

  constructor(
    private krkService: KrkService,
    private snackBar: MatSnackBar,
    private router: Router,
    private fb: FormBuilder,
    private formHelper: FormHelperService,
    private dictService: DictService,
    private ofwcaService: OfwcaService,
  ) {
    this.krkForm = this.fb.group({
      ofwca: this.ofwcaControl = fb.control('', [Validators.required]),
      payer: this.payerControl = fb.control('', [Validators.required]),
      status: this.statusControl = fb.control('', [Validators.required]),
      activeTo: this.activeToControl = fb.control(''),
      activeFrom: this.activeFromControl = fb.control(''),
      activeFee: this.activeFeeControl = fb.control(false),
    });
  }

  ngOnInit(): void {
    if (this.krk) {
      this.krk
        .pipe(takeUntil(this.destroyed$))
        .subscribe((krk: Krk) => {
          this.krkObject = krk;
          this.initialize(krk);
        });

      this.statusControl.valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe((dictId: number) => {
          this.dictService.getKey(dictId)
            .subscribe((dictKey: string) => {
              if (KRK_STATUS_ACTIVE === dictKey) {
                this.updateDates();
              }
            });
        });
    } else {
      if (this.ofwcaObject) {
        this.updatePayer(this.ofwcaObject.uuid);
      } else {
        this.dictService.getId(KRK_PAYER_ASF, DICT_TYPE_KRK_PAYER)
          .subscribe((dictId: number) => this.payerControl.setValue(dictId));
      }

      this.statusControl.disable();
      this.activeToControl.disable();
      this.initialized.next(true);
    }

    this.ofwcaControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((ofwcaId: string) => this.updatePayer(ofwcaId));

    this.activeFromControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        skipUntil(this.initialized)
      ).subscribe((activeFrom) => {
        if (!activeFrom) {
          return;
        }
        const activeToDate = moment(activeFrom).clone().add(3, 'month').subtract(1, 'day');
        this.activeToControl.setValue(activeToDate);

        this.dictService.getId(
          (moment().isBetween(activeFrom, activeToDate) ? KRK_STATUS_ACTIVE : KRK_STATUS_INACTIVE),
          DICT_TYPE_KRK_STATUS,
        ).subscribe((id: number) => this.statusControl.setValue(id));
    });

    if (this.ofwcaObject) {
      this.ofwcaControl.setValue(this.ofwcaObject.uuid);
    }
  }

  updatePayer(ofwcaId: string): void {
    this.ofwcaService.get(ofwcaId)
      .subscribe((ofwca: Ofwca) => {
        if (ofwca.krkRequestedByOfwca) {
          this.dictService.getId(KRK_PAYER_PARTNER, DICT_TYPE_KRK_PAYER)
            .subscribe((dictId: number) => this.payerControl.setValue(dictId));
        } else {
          this.dictService.getId(KRK_PAYER_ASF, DICT_TYPE_KRK_PAYER)
            .subscribe((dictId: number) => this.payerControl.setValue(dictId));
        }
      });
  }

  updateDates(): void {
    if (this.activeFromControl.value) {
      return;
    }

    const activeToDate = new Date();
    activeToDate.setMonth(activeToDate.getMonth() + 3);
    activeToDate.setDate(activeToDate.getDate() - 1);
    const activeFromDate = new Date();
    this.activeToControl.setValue(activeToDate);
    this.activeFromControl.setValue(activeFromDate);
  }

  initialize(krk: Krk): void {
    of(krk)
      .pipe(
        delay(0),
        tap(() => {
          this.krkForm.reset({
            payer: krk.payer?.id,
            status: krk.status?.id,
            activeTo: krk.activeTo ? moment(krk.activeTo) : '',
            activeFrom: krk.activeFrom ? moment(krk.activeFrom) : '',
            ofwca: krk.ofwca?.uuid,
            activeFee: krk.activeFee,
          });
          this.initialized.next(true);
        })
      ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  save(): void {
    this.processingRequest = true;
    this.krkService.sendForm(
      this.krkForm.getRawValue(),
      this.krkObject ? this.krkObject.uuid : null
    )
      .pipe(
        first()
      )
      .subscribe((response: Uuid) => {
        this.snackBar.open(this.krkObject ? 'Pomyślnie zaktualizowano KRK' : 'Pomyślnie utworzono KRK', '', {
          duration: 2000,
        });
        this.processingRequest = false;

        if (this.dialogRef) {
          this.krkCreated.emit();
          this.dialogRef.close();
        } else if (this.krk) {
          this.closeModification.emit();
        } else {
          this.router.navigate(['/krk/szczegoly', response.uuid]);
        }
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;

        this.snackBar.open('Podczas przesyłania formularza wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });

        if (error.status === 400) {
          this.errors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.krkForm, this.errors);
        }
      });
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }

    if (!this.krkObject) {
      this.router.navigate(['/krk']);
    } else {
      this.closeModification.emit();
    }
  }

}
